<script setup lang="ts">
import type { GuestbookMessage } from "@/models/message"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { deleteGuestbookMessage } from "@/api"
import { BaseModal, Button, Card, Icon, ReadMore } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MessageDeleteModalKey } from "@/symbols"

const props = defineProps<{
  message?: GuestbookMessage
  eventVaultId?: number
}>()

const emit = defineEmits<{
  (e: "success"): void
  (e: "error"): void
}>()

const { toggle } = useModal(MessageDeleteModalKey)
const { t } = useTranslation()

const client = useQueryClient()
const { mutate: deleteMessage, isPending } = useMutation({
  mutationFn: (message: GuestbookMessage) => deleteGuestbookMessage(message, props.eventVaultId),
  onSuccess: async () => {
    await client.invalidateQueries({ queryKey: ["listMessages"] })
    toggle()
    emit("success")
  },
  onError: () => {
    emit("error")
  },
})

function onDelete(message?: GuestbookMessage) {
  if (!message?.id)
    return
  deleteMessage(message)
}
</script>

<template>
  <BaseModal v-if="message" :name="MessageDeleteModalKey">
    <Card>
      <template #header>
        <div class="gpx-message-tile-header">
          <h2 class="gpx-message-tile-title">
            {{ t("message_tile.delete_title") }}
          </h2>
          <Button variant="flat" class="gpx-message-tile-close" @click="toggle">
            <Icon icon="Close" />
            <span class="sr-only">{{ t("message_delete_modal.close") }}</span>
          </Button>
        </div>
      </template>
      <transition name="gpx-fade" mode="out-in">
        <div v-if="isPending" class="gpx-message-tile_loading">
          <img src="@/assets/gpx-loading.gif" :alt="t('loading_alt')">
        </div>
        <div v-else>
          <p class="gpx-delete-media-desc">
            {{
              message.isCreator
                ? t("message_tile.delete_own_message")
                : t("message_tile.delete_message", { from: message.fromName })
            }}
          </p>
          <ReadMore v-if="message.message" :content="message.message" :lines="3" />
          <div class="gpx-delete-message-actions">
            <Button variant="flat" @click="toggle">
              {{ t("cancel") }}
            </Button>
            <Button variant="danger" @click="() => onDelete(message)">
              {{ t("confirm") }}
            </Button>
          </div>
        </div>
      </transition>
    </Card>
  </BaseModal>
</template>

<style scoped>
.gpx-message-tile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1.25rem;
}

.gpx-message-tile-title {
  font-family: Comfortaa, "Open Sans", sans-serif;
  font-weight: bold;
  font-size: 1.375rem;
  padding: 0;
  margin: 0;
}

.gpx-message-tile-close {
  padding: 0;
}

.gpx-delete-media-desc {
  margin: 0 0 1.5rem;
  padding: 0;
  font-family: "Open Sans", sans-serif;
  font-size: 1.125rem;
}

.gpx-delete-message-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  width: 100%;
}

.gpx-message-tile_loading {
  display: flex;
  place-content: center center;
}
</style>
